import { useMemo } from 'react';

/**
 * Hook to combine and manage loading states across Capture Tools
 * Provides a unified loading indicator for actual read/write operations
 * 
 * @param {Object} params - Loading states from various hooks
 * @param {boolean} params.portfolioLoading - Portfolio data loading state
 * @param {boolean} params.propertyLoading - Property list loading state
 * @param {boolean} params.responsesLoading - Responses loading state
 * @param {boolean} params.uploading - Image upload in progress
 * @returns {Object} - Unified loading state and operation-specific states
 */
export function useCaptureToolsLoading({
    portfolioLoading = false,
    propertyLoading = false,
    responsesLoading = false,
    uploading = false,
} = {}) {
    // Determine if any actual data operation is in progress
    const isLoading = useMemo(() => {
        return portfolioLoading || propertyLoading || responsesLoading || uploading;
    }, [portfolioLoading, propertyLoading, responsesLoading, uploading]);

    // Determine the loading message based on what's happening
    const loadingMessage = useMemo(() => {
        if (uploading) return 'Uploading...';
        if (portfolioLoading) return 'Loading portfolio...';
        if (propertyLoading) return 'Loading properties...';
        if (responsesLoading) return 'Loading responses...';
        return '';
    }, [portfolioLoading, propertyLoading, responsesLoading, uploading]);

    // Determine loading type for potential styling differences
    const loadingType = useMemo(() => {
        if (uploading) return 'upload';
        if (portfolioLoading || propertyLoading || responsesLoading) return 'fetch';
        return null;
    }, [portfolioLoading, propertyLoading, responsesLoading, uploading]);

    return {
        isLoading,
        loadingMessage,
        loadingType,
        // Expose individual states for granular control if needed
        states: {
            portfolioLoading,
            propertyLoading,
            responsesLoading,
            uploading,
        },
    };
}
